import React, { useEffect, useMemo, useState } from "react";
import { BarChart3, Download } from "lucide-react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import Dropdown from "src/components/ui/Dropdown";
import FilterSelect from "src/components/ui/FilterSelect";
import SearchInput, { SearchInputOption } from "src/components/ui/SearchInput";
import TableV2 from "src/components/ui/TableV2";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as reportActions from "src/redux/slices/reportSlice";
import * as warehouseActions from "src/redux/slices/warehouseSlice";
import useAssignedWarehouse from "src/hooks/useAssignedWarehouse";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";
import productApis from "src/api/product";
import customerApis from "src/api/customer";
import supplierApis from "src/api/supplier";
import reportApis from "src/api/report";
import { currencyFormatter } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";
import { ReportType } from "src/types/report.types";
import { Product } from "src/types/product.types";
import { Customer } from "src/types/customer.types";
import { Supplier } from "src/types/supplier.types";

const REPORT_TYPE_OPTIONS: { label: string; value: ReportType }[] = [
  { label: "Sales", value: "sales" },
  { label: "Purchase", value: "purchase" },
  { label: "Stock", value: "stock" },
  { label: "Profit", value: "profit" },
  { label: "Inventory Valuation", value: "inventory-valuation" },
  { label: "Stock Transfer", value: "transfer" },
];

// Each report type has its own dedicated permission (under the Reports
// group in the Designation matrix) rather than borrowing view access from
// the Sales/Purchases/Inventory modules — a role can run the Sales report
// without also being able to see, say, full stock data, and vice versa.
const REPORT_TYPE_ACCESS: Record<ReportType, string> = {
  sales: "reportsSales",
  purchase: "reportsPurchase",
  stock: "reportsStock",
  profit: "reportsProfit",
  "inventory-valuation": "reportsInventoryValuation",
  transfer: "reportsTransfer",
};

const EXPORTABLE_TYPES: ReportType[] = ["sales", "purchase", "stock", "transfer"];

const SUMMARY_CARDS: Record<
  ReportType,
  { key: string; label: string; currency?: boolean }[]
> = {
  sales: [
    { key: "orderCount", label: "Order Count" },
    { key: "totalQuantity", label: "Total Quantity" },
    { key: "totalAmount", label: "Total Amount", currency: true },
  ],
  purchase: [
    { key: "orderCount", label: "Order Count" },
    { key: "totalQuantity", label: "Total Quantity" },
    { key: "totalAmount", label: "Total Amount", currency: true },
  ],
  stock: [
    { key: "locationCount", label: "Location Count" },
    { key: "totalQuantity", label: "Total Quantity" },
    { key: "totalAvailable", label: "Total Available" },
  ],
  profit: [
    { key: "totalRevenue", label: "Total Revenue", currency: true },
    { key: "totalCost", label: "Total Cost", currency: true },
    { key: "totalProfit", label: "Total Profit", currency: true },
    { key: "orderCount", label: "Order Count" },
  ],
  "inventory-valuation": [
    { key: "locationCount", label: "Location Count" },
    { key: "totalQuantity", label: "Total Quantity" },
    { key: "totalValuation", label: "Total Valuation", currency: true },
  ],
  transfer: [
    { key: "transferCount", label: "Transfer Count" },
    { key: "lineCount", label: "Line Count" },
    { key: "totalQuantity", label: "Total Quantity" },
  ],
};

const idCell = (value: any) => (
  <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
    {value || "-"}
  </span>
);

const REPORT_COLUMNS: Record<ReportType, any[]> = {
  sales: [
    { key: "orderId", header: "Order ID" },
    {
      key: "date",
      header: "Date",
      accessor: ({ row }: any) => formatDateTime(row.date, "D MMM YYYY, HH:mm"),
    },
    { key: "status", header: "Status" },
    {
      key: "orderTotal",
      header: "Order Total",
      accessor: ({ row }: any) => currencyFormatter(row.orderTotal || 0),
    },
    {
      key: "customerId",
      header: "Customer ID",
      accessor: ({ row }: any) => idCell(row.customerId),
    },
    { key: "customerName", header: "Customer Name" },
    {
      key: "productId",
      header: "Product ID",
      accessor: ({ row }: any) => idCell(row.productId),
    },
    { key: "productName", header: "Product Name" },
    { key: "productSku", header: "Product SKU" },
    { key: "quantity", header: "Quantity" },
    {
      key: "sellingPrice",
      header: "Selling Price",
      accessor: ({ row }: any) => currencyFormatter(row.sellingPrice || 0),
    },
    {
      key: "lineTotal",
      header: "Line Total",
      accessor: ({ row }: any) => currencyFormatter(row.lineTotal || 0),
    },
  ],
  purchase: [
    { key: "orderId", header: "Order ID" },
    {
      key: "date",
      header: "Date",
      accessor: ({ row }: any) => formatDateTime(row.date, "D MMM YYYY, HH:mm"),
    },
    { key: "status", header: "Status" },
    {
      key: "orderTotal",
      header: "Order Total",
      accessor: ({ row }: any) => currencyFormatter(row.orderTotal || 0),
    },
    {
      key: "supplierId",
      header: "Supplier ID",
      accessor: ({ row }: any) => idCell(row.supplierId),
    },
    { key: "supplierName", header: "Supplier Name" },
    {
      key: "productId",
      header: "Product ID",
      accessor: ({ row }: any) => idCell(row.productId),
    },
    { key: "productName", header: "Product Name" },
    { key: "productSku", header: "Product SKU" },
    { key: "quantity", header: "Quantity" },
    {
      key: "buyingPrice",
      header: "Buying Price",
      accessor: ({ row }: any) => currencyFormatter(row.buyingPrice || 0),
    },
    {
      key: "lineTotal",
      header: "Line Total",
      accessor: ({ row }: any) => currencyFormatter(row.lineTotal || 0),
    },
  ],
  stock: [
    { key: "id", header: "ID", accessor: ({ row }: any) => idCell(row.id) },
    {
      key: "productId",
      header: "Product ID",
      accessor: ({ row }: any) => idCell(row.productId),
    },
    {
      key: "warehouseId",
      header: "Warehouse ID",
      accessor: ({ row }: any) => idCell(row.warehouseId),
    },
    { key: "quantity", header: "Quantity" },
    { key: "reservedQuantity", header: "Reserved Quantity" },
    { key: "availableQuantity", header: "Available Quantity" },
    { key: "productName", header: "Product Name" },
    { key: "productSku", header: "Product SKU" },
    {
      key: "costPrice",
      header: "Cost Price",
      accessor: ({ row }: any) => currencyFormatter(row.costPrice || 0),
    },
  ],
  profit: [
    { key: "orderId", header: "Order ID" },
    { key: "invoiceNo", header: "Invoice No" },
    {
      key: "date",
      header: "Date",
      accessor: ({ row }: any) => formatDateTime(row.date, "D MMM YYYY, HH:mm"),
    },
    { key: "customerName", header: "Customer Name" },
    {
      key: "productId",
      header: "Product ID",
      accessor: ({ row }: any) => idCell(row.productId),
    },
    { key: "productName", header: "Product Name" },
    { key: "productSku", header: "Product SKU" },
    { key: "quantity", header: "Quantity" },
    {
      key: "sellingPrice",
      header: "Selling Price",
      accessor: ({ row }: any) => currencyFormatter(row.sellingPrice || 0),
    },
    {
      key: "costPrice",
      header: "Cost Price",
      accessor: ({ row }: any) => currencyFormatter(row.costPrice || 0),
    },
    {
      key: "profit",
      header: "Profit",
      accessor: ({ row }: any) =>
        currencyFormatter(
          (row.lineTotal || 0) - (row.costPrice || 0) * (row.quantity || 0)
        ),
    },
  ],
  "inventory-valuation": [
    {
      key: "productId",
      header: "Product ID",
      accessor: ({ row }: any) => idCell(row.productId),
    },
    {
      key: "warehouseId",
      header: "Warehouse ID",
      accessor: ({ row }: any) => idCell(row.warehouseId),
    },
    { key: "productName", header: "Product Name" },
    { key: "productSku", header: "Product SKU" },
    {
      key: "costPrice",
      header: "Cost Price",
      accessor: ({ row }: any) => currencyFormatter(row.costPrice || 0),
    },
    { key: "warehouseName", header: "Warehouse Name" },
    { key: "quantity", header: "Quantity" },
    {
      key: "valuation",
      header: "Valuation",
      accessor: ({ row }: any) => currencyFormatter(row.valuation || 0),
    },
  ],
  transfer: [
    { key: "transferId", header: "Transfer ID" },
    {
      key: "date",
      header: "Date",
      accessor: ({ row }: any) => formatDateTime(row.date, "D MMM YYYY, HH:mm"),
    },
    { key: "fromWarehouseName", header: "From Warehouse" },
    { key: "toWarehouseName", header: "To Warehouse" },
    {
      key: "productId",
      header: "Product ID",
      accessor: ({ row }: any) => idCell(row.productId),
    },
    { key: "productName", header: "Product Name" },
    { key: "productSku", header: "Product SKU" },
    { key: "quantity", header: "Quantity" },
    { key: "createdByName", header: "Created By" },
  ],
};

// Plain-object row shape used for CSV/Excel export — mirrors the visible
// table columns above but with header-case keys and unformatted numbers.
const toExportRow = (type: ReportType, row: any): Record<string, any> => {
  switch (type) {
    case "sales":
      return {
        "Order ID": row.orderId,
        Date: row.date,
        Status: row.status,
        "Order Total": row.orderTotal,
        "Customer ID": row.customerId,
        "Customer Name": row.customerName,
        "Product ID": row.productId,
        "Product Name": row.productName,
        "Product SKU": row.productSku,
        Quantity: row.quantity,
        "Selling Price": row.sellingPrice,
        "Line Total": row.lineTotal,
      };
    case "purchase":
      return {
        "Order ID": row.orderId,
        Date: row.date,
        Status: row.status,
        "Order Total": row.orderTotal,
        "Supplier ID": row.supplierId,
        "Supplier Name": row.supplierName,
        "Product ID": row.productId,
        "Product Name": row.productName,
        "Product SKU": row.productSku,
        Quantity: row.quantity,
        "Buying Price": row.buyingPrice,
        "Line Total": row.lineTotal,
      };
    case "stock":
      return {
        ID: row.id,
        "Product ID": row.productId,
        "Warehouse ID": row.warehouseId,
        Quantity: row.quantity,
        "Reserved Quantity": row.reservedQuantity,
        "Available Quantity": row.availableQuantity,
        "Product Name": row.productName,
        "Product SKU": row.productSku,
        "Cost Price": row.costPrice,
      };
    case "transfer":
      return {
        "Transfer ID": row.transferId,
        Date: row.date,
        "From Warehouse": row.fromWarehouseName,
        "To Warehouse": row.toWarehouseName,
        "Product ID": row.productId,
        "Product Name": row.productName,
        "Product SKU": row.productSku,
        Quantity: row.quantity,
        "Created By": row.createdByName,
      };
    default:
      return row;
  }
};

const ReportsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { runReport } = useAppSelector((state) => state.report);
  const { warehouseList } = useAppSelector((state) => state.warehouse);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = useMemo(
    () => user?.roleDetails?.permissions || user?.permissions || {},
    [user?.roleDetails?.permissions, user?.permissions]
  );
  const isTenantAdmin =
    user?.role === "admin" || user?.roleDetails?.name?.toLowerCase() === "admin";

  // Only offer report types the current role has been granted view access
  // to (tenant admins always see every type).
  const availableReportTypes = useMemo(
    () =>
      REPORT_TYPE_OPTIONS.filter(
        (opt) =>
          isTenantAdmin ||
          (permissions?.[REPORT_TYPE_ACCESS[opt.value]] || []).includes("view")
      ),
    [isTenantAdmin, permissions]
  );

  const [reportType, setReportType] = useState<ReportType>("sales");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [productId, setProductId] = useState("");
  const [productLabel, setProductLabel] = useState("");
  const { assignedWarehouseId, isWarehouseLocked } = useAssignedWarehouse();
  const { singular: warehouseLabel } = useWarehouseLabel();
  const [warehouseId, setWarehouseId] = useState("");
  const [fromWarehouseId, setFromWarehouseId] = useState("");
  const [toWarehouseId, setToWarehouseId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customerLabel, setCustomerLabel] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [supplierLabel, setSupplierLabel] = useState("");
  const [exportFormat, setExportFormat] = useState<"csv" | "excel">("csv");
  const [exporting, setExporting] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  useEffect(() => {
    dispatch(warehouseActions.getWarehouseListRequest({ page: 1, limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (isWarehouseLocked && assignedWarehouseId) setWarehouseId(assignedWarehouseId);
  }, [isWarehouseLocked, assignedWarehouseId]);

  // If the current selection isn't in the allowed set (e.g. the default
  // "sales" isn't permitted for this role), fall back to the first
  // report type the user can actually run.
  useEffect(() => {
    if (
      availableReportTypes.length &&
      !availableReportTypes.some((opt) => opt.value === reportType)
    ) {
      setReportType(availableReportTypes[0].value);
    }
  }, [availableReportTypes, reportType]);

  const warehouseOptions = (warehouseList?.data?.data || []).map((w: any) => ({
    label: w.name,
    value: w._id,
  }));

  const currentParams = useMemo(
    () => ({
      type: reportType,
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      ...(productId ? { productId } : {}),
      ...(reportType === "transfer"
        ? {
            ...(fromWarehouseId ? { fromWarehouseId } : {}),
            ...(toWarehouseId ? { toWarehouseId } : {}),
          }
        : warehouseId
          ? { warehouseId }
          : {}),
      ...(customerId ? { customerId } : {}),
      ...(supplierId ? { supplierId } : {}),
    }),
    [
      reportType,
      dateFrom,
      dateTo,
      productId,
      warehouseId,
      fromWarehouseId,
      toWarehouseId,
      customerId,
      supplierId,
    ]
  );

  const handleRunReport = (page = 1) => {
    setHasRun(true);
    dispatch(reportActions.runReportRequest({ ...currentParams, page, limit: 10 }));
  };

  const handlePageChange = (page: number) => handleRunReport(page);

  const isExportable = EXPORTABLE_TYPES.includes(reportType);

  const handleExport = async () => {
    try {
      setExporting(true);
      const res = await reportApis.runReport({ ...currentParams, limit: "all" });
      const rows: any[] = res?.data || [];
      if (!rows.length) {
        toast.info("No rows to export for the current filters");
        return;
      }
      const exportRows = rows.map((r) => {
        const row = toExportRow(reportType, r);
        const hasWarehouseColumn = Object.keys(row).some((k) => k.includes("Warehouse"));
        if (!hasWarehouseColumn) return row;
        const relabeled: Record<string, any> = {};
        Object.entries(row).forEach(([k, v]) => {
          relabeled[k.replace("Warehouse", warehouseLabel)] = v;
        });
        return relabeled;
      });
      const worksheet = XLSX.utils.json_to_sheet(exportRows);

      if (exportFormat === "csv") {
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${reportType}-report.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
        XLSX.writeFile(workbook, `${reportType}-report.xlsx`);
      }
    } catch (error) {
      toast.error("Failed to export report");
    } finally {
      setExporting(false);
    }
  };

  const summary = runReport.data?.summary;
  const rows = runReport.data?.data || [];
  const pagination = runReport.data?.pagination;
  const columns = REPORT_COLUMNS[reportType].map((c: any) =>
    /Warehouse/.test(c.header)
      ? { ...c, header: c.header.replace(/Warehouse/, warehouseLabel) }
      : c
  );

  return (
    <DashboardLayout
      meta={{
        title: "Reports | Invyntra",
        description: "Analytics and business reports.",
        keywords: "reports, analytics, sales, purchase, stock, profit, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
            <BarChart3 size={26} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Reports
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Analytics and business reports. Select a report type and optional
              filters, then run.
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 items-end">
            <FilterSelect
              variant="boxed"
              label="Report type"
              value={reportType}
              onChange={(val) => setReportType(val as ReportType)}
              allLabel="Select report type"
              options={availableReportTypes}
            />
            <Input
              label="Date from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <Input
              label="Date to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
            <Button onClick={() => handleRunReport(1)} isLoading={runReport.loading}>
              Run report
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SearchInput<Product>
              label="Product (optional)"
              placeholder="Filter by product"
              value={productLabel}
              onSearch={async (query) => {
                const res = await productApis.getProductList({
                  page: 1,
                  limit: 10,
                  search: query,
                });
                const list: Product[] = res?.data?.data || res?.data || [];
                return list.map((p) => ({
                  value: p._id || "",
                  label: `${p.name} (${p.sku || p.productId})`,
                  data: p,
                }));
              }}
              onSelect={(option: SearchInputOption<Product>) => {
                setProductId(option.value);
                setProductLabel(option.label);
              }}
              onClear={() => {
                setProductId("");
                setProductLabel("");
              }}
            />
            {reportType === "transfer" ? (
              <>
                <FilterSelect
                  variant="boxed"
                  label={`From ${warehouseLabel} (optional)`}
                  value={fromWarehouseId}
                  onChange={setFromWarehouseId}
                  allLabel={`Any ${warehouseLabel.toLowerCase()}`}
                  options={warehouseOptions}
                />
                <FilterSelect
                  variant="boxed"
                  label={`To ${warehouseLabel} (optional)`}
                  value={toWarehouseId}
                  onChange={setToWarehouseId}
                  allLabel={`Any ${warehouseLabel.toLowerCase()}`}
                  options={warehouseOptions}
                />
              </>
            ) : (
              <>
                <FilterSelect
                  variant="boxed"
                  label={`${warehouseLabel} (optional)`}
                  value={warehouseId}
                  onChange={setWarehouseId}
                  allLabel={`Filter by ${warehouseLabel.toLowerCase()}`}
                  options={warehouseOptions}
                  disabled={isWarehouseLocked}
                  helperText={
                    isWarehouseLocked
                      ? `Locked to your assigned ${warehouseLabel.toLowerCase()}.`
                      : undefined
                  }
                />
                <SearchInput<Customer>
                  label="Customer (optional)"
                  placeholder="Filter by customer"
                  value={customerLabel}
                  onSearch={async (query) => {
                    const res = await customerApis.getCustomersList({
                      page: 1,
                      limit: 10,
                      search: query,
                    });
                    const list: Customer[] = res?.data || [];
                    return list.map((c) => ({
                      value: c._id || "",
                      label: `${c.name} (${c.mobile})`,
                      data: c,
                    }));
                  }}
                  onSelect={(option: SearchInputOption<Customer>) => {
                    setCustomerId(option.value);
                    setCustomerLabel(option.label);
                  }}
                  onClear={() => {
                    setCustomerId("");
                    setCustomerLabel("");
                  }}
                />
                <SearchInput<Supplier>
                  label="Supplier (optional)"
                  placeholder="Filter by supplier"
                  value={supplierLabel}
                  onSearch={async (query) => {
                    const res = await supplierApis.getSupplierList({
                      page: 1,
                      limit: 10,
                      search: query,
                    });
                    const list: Supplier[] = res?.data?.data || res?.data || [];
                    return list.map((s) => ({
                      value: s._id || "",
                      label: s.name || "",
                      data: s,
                    }));
                  }}
                  onSelect={(option: SearchInputOption<Supplier>) => {
                    setSupplierId(option.value);
                    setSupplierLabel(option.label);
                  }}
                  onClear={() => {
                    setSupplierId("");
                    setSupplierLabel("");
                  }}
                />
              </>
            )}
          </div>

          <div className="border-t border-gray-100 pt-4 dark:border-gray-700">
            {isExportable ? (
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Download the same report shape as CSV or Excel (uses date
                  range above).
                </p>
                <Dropdown
                  value={exportFormat}
                  options={[
                    { label: "CSV", value: "csv" },
                    { label: "Excel", value: "excel" },
                  ]}
                  onChange={(val) => setExportFormat(val as "csv" | "excel")}
                />
                <Button
                  variant="outline"
                  onClick={handleExport}
                  isLoading={exporting}
                  disabled={exporting}
                >
                  <Download size={16} className="mr-1.5" />
                  Download export
                </Button>
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                File export is available when report type is Sales, Purchase,
                Stock, or Stock Transfer.
              </p>
            )}
          </div>
        </Card>

        {/* Results */}
        {!hasRun ? (
          <Card>
            <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              Choose a report type and click "Run report" to load data.
            </p>
          </Card>
        ) : (
          <>
            {summary && (
              <Card>
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Summary
                </h2>
                <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
                  {SUMMARY_CARDS[reportType].map((item) => (
                    <div key={item.key}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {item.label}
                      </p>
                      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
                        {item.currency
                          ? currencyFormatter(summary[item.key] || 0)
                          : (summary[item.key] ?? 0).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            <Card>
              <TableV2
                columns={columns}
                data={rows}
                pagination={pagination as any}
                onPageChange={handlePageChange}
                loading={runReport.loading}
              />
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
